import { Check } from "lucide-react";
import { Kicker, Big, Lines } from "./ui";
import { Cta, Micro } from "./cta";

const INCLUDES = [
  ["WTF Idea + Consistent Cash", "Find the belief-shifting idea worth building your business around."],
  ["Vision + Capacity", "Get clear on the vision you’re actually building toward — and become capable of holding it."],
  ["Blueprint", "Turn your experience, expertise and beliefs into your own unique method."],
  ["Author Authority", "Speak your book in hours, turn your message into authority and connect the book to your business."],
  ["Create + Sell $10K+ Offers", "Package your transformation into a high-value offer people actually want."],
  ["AI + GoHighLevel Systems", "Build the backend, CRM, follow-up and automation that keeps the business moving."],
  ["Human Design + Gene Keys", "Build around how you’re actually designed to lead, communicate and sell."],
  ["Course Creation", "Turn your method into curriculum and scalable intellectual property."],
  ["Embodiment + Receiving", "Work on the person behind the business — capacity, identity, intimacy, receiving and expression."],
];

const BUILDS = ["How a client got clear on $100K vision — then secured the investment on the day on the vision board", "How she sold $10K offers using the Million Dollar Sales Script", "How she spoke her book in 3 hours without AI — then went from $897 to $10K offers sold before the book was published", "How I built a dentist’s funnel in just 2 hours", "The exact AI platforms, automations + course systems I use", "How embodiment + tantra create space to receive", "How I weave Human Design + Gene Keys through the book, brand, offer, and entire business"];

const BundleImage = () => (
  <>
    <img className="bundle-img bundle-full" src="/img/bootcamp-bundle.webp" alt="$100K Book Bootcamp: Speak Your Book in 3 Hours, Create & Sell $10K+ Offers, Freedom Funnel, AI Powered System, Human Design Alignment and Quantum Codes Gene Keys for Business, plus the 5 steps" loading="lazy" data-testid="bootcamp-image" />
    <div className="bundle-phone" data-testid="bootcamp-image-phone">
      <img src="/img/bootcamp-products.webp" alt="$100K Book Bootcamp course bundle" loading="lazy" />
      <div className="bundle-scroll" data-testid="bootcamp-steps-scroll">
        <img src="/img/bootcamp-steps.webp" alt="The 5 steps: Decide, Blueprint, Embody, Systemize (sell + scale) and Transmit" loading="lazy" />
      </div>
      <p className="bundle-hint">Swipe to see all 5 steps →</p>
    </div>
  </>
);

/* $100K BOOK BOOTCAMP */
export const Bootcamp = () => (
  <section id="bootcamp" className="sec narrow center" data-testid="bootcamp-section">
    <Kicker>Ready to build yours?</Kicker>
    <Big className="huge">The $100K<br /><span className="gold-text">Book Bootcamp</span></Big>
    <p className="sub-h">100 days. The entire library. Build your business.</p>
    <BundleImage />
    <Lines items={["This isn’t another course you’re buying to watch “someday.”", "For the next 100 days, you unlock Dasha’s complete Love University training library — the same ecosystem behind the WTF Idea → Method → Book → High-Value Offer → AI Freedom Funnel model."]} />
    <p className="good dark">This is for action takers only.</p>
    <Lines className="q" items={["You don’t need to finish every course.", "You don’t need more information.", "You need to use what you need to BUILD."]} />

    <div className="builds" data-testid="bootcamp-includes">
      <p className="builds-h">Your 100-day access includes:</p>
      {INCLUDES.map(([h, t]) => <p key={h}><Check size={16} /> <span><b className="inc-h">{h}</b>{t}</span></p>)}
      <p className="inc-more">And more from Dasha’s complete training library.</p>
    </div>
    <img className="wide-img" src="/img/library-100day.webp" alt="Your 100-day DIY access includes the entire Love University library: Consistent Cash, Capacity, Quantum Habits, Blueprint, Alignment with Human Design, Gene Keys Mastery, Author Authority, Create and Sell 10K plus offers, AMP / Go High Level, Create your course, Tantra and Receive" loading="lazy" data-testid="library-image" />

    <Big as="p" className="mt">This is not a lifetime membership.<br /><span className="gold-text">It’s a 100-day build window.</span></Big>
    <Lines items={["You have 100 days of access to go in, get what you need and MOVE."]} />
    <Lines className="q" items={["Find the idea.", "Build the method.", "Speak the book.", "Create the offer.", "Build the funnel.", "Automate the backend.", "Get it into the world."]} />

    <div className="builds" data-testid="bootcamp-builds">
      <p className="builds-h">Don’t just learn the strategy. <span className="gold-text">Watch me build it with real clients.</span></p>
      {BUILDS.map((t) => <p key={t}><Check size={16} /> {t}</p>)}
    </div>

    <Big as="p" className="mt">Don’t collect the information.<br /><span className="gold-text">Implement it.</span></Big>
    <div className="price-block" data-testid="bootcamp-price">
      <p className="price-n">$100</p>
      <p className="price-l">100 days • Complete Love University access</p>
      <Cta id="bootcamp-cta" only="bootcamp" bootcampLabel="Unlock the $100K Book Bootcamp" />
      <p className="price-tag">This is NOT about making $100K from book royalties — and it’s NOT a promise that you’ll make $100K in 100 days.</p>
    </div>
    <p className="disclaimer">All training is provided for educational purposes. Individual results vary.</p>
  </section>
);

/* BUILD IT WITH DASHA */
export const BuildWithDasha = () => (
  <section className="dark-band" data-testid="build-with-dasha-section">
    <div className="sec split">
      <img className="portrait" src="/img/dasha-hero.webp" alt="Dasha Love" loading="lazy" />
      <div>
        <Kicker light>Want more than DIY?</Kicker>
        <Big className="light">Build it <span className="gold-text">with Dasha.</span></Big>
        <p className="sub-h light">NO BS Life &amp; Business — 8-Week Challenge</p>
        <Lines className="light" items={["The $100K Book Bootcamp gives you the system.", "The 8-week experience is where we implement it together.", "Bring Dasha:"]} />
        <Lines className="light q" items={["The crazy idea.", "The unfinished book.", "The scattered offers.", "The unclear method.", "The business that’s become too complicated.", "The message you’ve been scared to say."]} />
        <Lines className="light" items={["Together, turn the pieces into ONE connected business."]} />
        <p className="price-l light" data-testid="dasha-price">$2,000 • 8 weeks</p>
        <Cta id="dasha-cta" only="dasha" />
      </div>
    </div>
    <div className="sec narrow center walk">
      <Kicker light>An example of what you walk away with</Kicker>
      <img className="walk-img" src="/img/walk-away-example.webp" alt="Example: One message, a complete business. The DBEST Method ladder from free content to a $37 audio activation, a $997 course, a $2,222 1:1 session and an $18,000 mastermind" loading="lazy" data-testid="walk-away-image" />
      <p className="disclaimer light-d">Example offer ecosystem. Prices shown are examples, not results. Individual results vary.</p>
    </div>
  </section>
);
